import { Component, ComponentFactoryResolver, Input, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
// Service
import { EditableTableService } from './service/editable-table.service';
import { BroadcastService, ModalService, ModalSize } from "@cv-invision/core";
import { GenericFormService } from '@cv-invision/cv.invision-views.ux';
// Model
import { DefaultRow, EditableTableActions } from './model/editable-table.model';
// Components
import { CreateUpdateFormComponent } from "../create-update-form/create-update-form.component";
import { Table } from 'primeng/table';
import { GenericTableModel } from "../../model/generic-table.model";

@Component({
	selector: 'cv-editable-table',
	templateUrl: './editable-table.component.html',
	styleUrls: ['./editable-table.component.scss'],
	providers: [ConfirmationService, MessageService]
})
export class EditableTableComponent implements OnInit, OnDestroy {
	@Input() label: string;
	@Input() tableConfig: GenericTableModel;
	@Input() modalSize: ModalSize = ModalSize.MD;
	@Input() model: any;
	@Input() formBuilderConfig: any;
	@ViewChild('dt') dataTable: any;

	public integrationParams = new GenericTableModel()
	public integrationExtraData: any = {};
	public defaultModel: DefaultRow = {
		disabled: false,
		getValue: (field: any, row: any) => {
			return row[field.key];
		}
	}
	public data: any = [];
	clonedData: any = [];
	searchField: string;

	get cellWidth(): number {
		return 100 / (this.model.length + 1);
	}

	private atComponentDestroy: Subject<any> = new Subject<any>();

	constructor(
		public confirmationService: ConfirmationService,
		public messageService: MessageService,
		protected service: EditableTableService,
		protected modalService: ModalService,
		protected componentFactoryResolver: ComponentFactoryResolver,
		protected broadcast: BroadcastService,
		public formService: GenericFormService
	) {
	}

	ngOnInit() {
		this.searchField = '';
		this.model = this.model.map((tmpModel: any) => {
			return { ...this.defaultModel, ...tmpModel }
		});

		this.formService.submitObserver
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data: any) => {
				if (data.submit) {
					const row = data.data;
					if (data.isEditing) {
						this.onRowEditSave(row);
					} else {
						if ('saveAsObject' in this.integrationExtraData && this.integrationExtraData.saveAsObject) {
							this.saveModel(row);
						} else {
							this.onRowSave(row);
						}
					}
				}
			});

		this.formService.onKeyFilterOptions$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data: any) => {
				const field = data.field;
				if (data && data.eventName === 'OnSelect') {
					this.formService
						.getDataByUrl(field.UIOptions.Url, field.UIOptions.FilterName, data.event.query)
						.pipe(takeUntil(this.atComponentDestroy))
						.subscribe(result => {
							this.formBuilderConfig[field.Name].options = result;
						});
				}
			});
	}

	/**
	 * Detect changes on values variable in order to option selected
	 * @param changes
	 */
	ngOnChanges(changes: SimpleChanges) {
		if (changes['model']) {
			this.model = changes['model'].currentValue.map((tmpModel: any) => {
				return { ...this.defaultModel, ...tmpModel }
			});
		}
		if (changes['params'] ||
			(
				changes['extraData'] &&
				'isExternalIntegration' in changes['extraData'].currentValue) &&
			changes['extraData'].currentValue.isExternalIntegration) {
			this.dataTable?.reset();
			this.searchField = '';
			this.loadData();
		}
	}

	/**
	 * Set the integration params
	 *
	 * @param params
	 */
	setIntegrationParamsData(params: GenericTableModel): void {
		Object.assign(this.integrationParams, params);
	}

	/**
	 * Set extraData used in the model
	 *
	 * @param data
	 */
	setIntegrationExtraData(data: any): void {
		Object.assign(this.integrationExtraData, data);
	}

	/**
	 * Get the data from the service
	 */
	loadData() {
		this.service
			.getData(this.integrationParams)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				this.data = [];
				let responseData = result.data ? result.data : result;
				if (this.tableConfig.resultKey) {
					responseData = result[this.tableConfig.resultKey]
				}

				if (responseData && responseData.length > 0) {
					if (this.tableConfig.stringKey) {
						const key = this.tableConfig.stringKey || 'name';
						this.data = responseData.map((value: string, index: number) => {
							return {
								id: index,
								[key]: value
							};
						});
					} else {
						this.data = responseData?.map((row: any, index: any) => {
							return {
								...{ id: index },
								...row
							}
						});
					}
				}
			});
	}

	/**
	 * Trigger the field onChange
	 *
	 * @param $event
	 * @param field
	 * @param row
	 */
	onChange($event: any, field: any, row?: any): void {
		if (field.onChange) {
			field.onChange($event, row, this.model, this.data, this.integrationExtraData);
		}
	}

	/**
	 * On RowEdit, add the modified data into the cloned array.
	 * @param row
	 */
	onRowEditInit(row: any) {
		this.service.onRowClick$.next({name: 'click', row});
		if ('editInModal' in this.integrationExtraData && this.integrationExtraData.editInModal) {
			this.addEditRecord(true, row);
		} else {
			this.clonedData[row.id] = { ...row };
		}

	}

	/**
	 * Save the data to the API
	 *
	 * @param row
	 */
	onRowEditSave(row: any): void {
		this.data[row.id] = { ...row };
		delete this.clonedData[row.id];
		let data = this.data;
		let customName;

		if ('saveAsObject' in this.integrationExtraData && this.integrationExtraData.saveAsObject) {
			data = row;
		}
		if (this.integrationParams.isCustomRecordData) {
			customName = this.integrationExtraData.customName;
		}
		this.service
			.saveData(this.integrationParams, data, customName)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe({
				next: result => {
					this.loadData();
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: `${this.tableConfig.title} was updated`
					});
				},
				error: error => {
					console.error(error);
				}
			});
	}

	/**
	 *
	 * @param row
	 */
	onRowSave(row: any): void {
		this.data.push(row);
		this.service
			.saveData(this.integrationParams, this.data, row)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe({
				next: result => {
					this.loadData();
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: `${this.tableConfig.title} was updated`
					});
				},
				error: error => {
					console.error(error);
				}
			});
	}

	/**
	 * Send a broadcast message when a ROW action it's triggered
	 *
	 * @param row
	 */
	onRowAction(action: string, row: any): void {
		this.broadcast.broadcast({
			name: EditableTableActions.TABLE_ROW_ACTION,
			event: {
				type: action,
				row
			}
		})
	}

	/**
	 * Show delete confirmation service
	 *
	 * @param row
	 */
	onShowDeleteConfirm(row: any) {
		this.confirmationService.confirm({
			message: 'Are you sure that you want to proceed?',
			header: 'Confirmation',
			icon: 'pi pi-exclamation-triangle',
			accept: () => {
				if ('saveAsObject' in this.integrationExtraData && this.integrationExtraData.saveAsObject) {
					this.deleteModel(row);
				} else {
					this.deleteRow(row);
				}

			},
			// reject: () => {
			// }
		});
	}

	/**
	 * Save the data to the API on delete
	 *
	 * @param row
	 */
	deleteRow(row: any) {
		this.data = this.data.filter((item: { id: any; }) => item.id !== row.id);
		this.service
			.saveData(this.integrationParams, this.data, row)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe({
				next: result => {
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: 'Product Type was deleted'
					});
				},
				error: error => {
					console.error(error);
				}
			});
	}

	/**
	 * Remove changes from cloned array if edit is cancelled
	 * @param row
	 * @param index
	 */
	onRowEditCancel(row: any, index: number) {
		this.data[index] = this.clonedData[row.id];
		delete this.clonedData[row.id];
	}

	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}

	/**
	 * Event to open modal to add or edit records
	 */
	addEditRecord(isEditing: boolean, data?: any): void {
		if (this.integrationParams.isCustomRecordData) {
			this.integrationExtraData.customName = data.name;
			this.service.getCustomData(this.integrationParams, data.name)
				.pipe(takeUntil(this.atComponentDestroy))
				.subscribe(result => {
					if (result) {
						this.openAddEditRecord(isEditing, result.data);
					}
				});
		} else {
			this.openAddEditRecord(isEditing, data);
		}
	}

	openAddEditRecord(isEditing: boolean, data?: any): void {
		this.modalService
			.open({
				componentFactoryResolver: this.componentFactoryResolver,
				component: CreateUpdateFormComponent,
				data: {
					isEditing: isEditing,
					modelData: data,
					formBuilderConfig: { ...this.formBuilderConfig },
					integrationExtraData: this.integrationExtraData,
					integrationParams: this.integrationParams,
					label: this.tableConfig.title
				},
				modalConfiguration: {
					title: `${this.integrationParams.isCustomRecordData ? this.integrationExtraData.customName : ''} 
							${this.tableConfig.addButtonText ? this.tableConfig.addButtonText : this.tableConfig.title}`,
					modalSize: this.modalSize || ModalSize.MD
				}
			})
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe(result => {
				//
			});
	}

	/**
	 * Even to call to save data in API
	 * @param row
	 */
	saveModel(row: any): void {
		this.service
			.savePostData(this.integrationParams, row)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe({
				next: result => {
					this.loadData();
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: `${this.tableConfig.title} was updated`
					});
				},
				error: error => {
					console.error(error);
				}
			});
	}

	/**
	 * Event to call to delete data from API
	 * @param row
	 */
	deleteModel(row: any) {
		this.service
			.deleteModel(this.integrationParams, row)
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe({
				next: result => {
					this.loadData();
					this.messageService.add({
						severity: 'success',
						summary: 'Success',
						detail: `${this.tableConfig.title} was deleted`
					});
				},
				error: error => {
					console.error(error);
				}
			});
	}

	/**
	 * Get fields for global filter
	 * @returns
	 */
	getGlobalFilters(): Array<any> {
		return this.model.map((field: { key: any; }) => field.key);
	}

	/**
	 * Apply Global Filter
	 * @param $event
	 * @param table
	 */
	applyGlobalFilter($event: any, table: Table): void {
		table.filterGlobal($event.target.value, 'contains');
	}

	/**
	 * Even to call when input switch is clicked
	 * @param $event
	 * @param row
	 * @param field
	 */
	releaseOnChange($event: any, row: any, field: any): void {
		this.service.onChangeOptions$.next({
			event: field.eventName,
			data: {
				value: row,
				status: $event.checked
			}
		})
	}

	/**
	 * custom events from model configuration
	 */
	customEvent(customAction: any, row: any): void {
		this.service.onCustomEvents$.next({ eventName: customAction.eventName, data: row });
	}
}
